import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import API from '../../../../services/API'

const emptyBase = () => ({
  name: '',
  address: {
    lineOne: '',
    town: '',
    county: '',
    postCode: '',
  },
  isNew: true,
})

const LocationStep = forwardRef((props, ref) => {
  const [locations, setLocations] = useState([])

  useEffect(() => {
    API.get('/base-location-options').then((res) => {
      if (res.status === 200 && res.data.length > 0) {
        setLocations(res.data)
      } else {
        setLocations([emptyBase()])
      }
    })
  }, [])

  useImperativeHandle(ref, () => ({
    save: async () => {
      try {
        for (const location of locations) {
          if (!location.name?.trim()) continue
          const res = await API.post(
            location.isNew ? '/new-base-location' : '/edit-base-location',
            {
              _id: location._id,
              name: location.name.trim(),
              address: location.address,
            },
          )
          // The shared axios instance resolves on every status, so a failed
          // save has to be caught here or it would silently pass.
          if (res.status !== 200) return false
        }
        return true
      } catch (e) {
        console.log(e)
        return false
      }
    },
  }))

  const updateLocation = (index, updates) =>
    setLocations((prev) =>
      prev.map((location, i) =>
        i === index ? { ...location, ...updates } : location,
      ),
    )

  const updateAddress = (index, field, value) =>
    setLocations((prev) =>
      prev.map((location, i) =>
        i === index
          ? { ...location, address: { ...location.address, [field]: value } }
          : location,
      ),
    )

  return (
    <Stack gap={2}>
      <Box>
        <Typography variant="h6">Where do you operate from?</Typography>
        <Typography variant="body2" color="text.secondary">
          Base locations are where your vehicles, equipment and staff are
          stationed. Give your first base a name (e.g. "HQ" or the town it's
          in) — you can add more bases now or later in Site Configuration.
        </Typography>
      </Box>

      {locations.map((location, index) => (
        <Paper key={location._id ?? `new-${index}`} variant="outlined">
          <Stack gap={1} p={2}>
            <Box display="flex" gap={1} alignItems="center">
              <TextField
                label="Base Name"
                fullWidth
                required
                value={location.name ?? ''}
                onChange={(e) => updateLocation(index, { name: e.target.value })}
              />
              {location.isNew && locations.length > 1 && (
                <IconButton
                  onClick={() =>
                    setLocations((prev) => prev.filter((_, i) => i !== index))
                  }
                >
                  <DeleteOutlineRoundedIcon />
                </IconButton>
              )}
            </Box>
            <TextField
              label="Address Line 1"
              fullWidth
              value={location.address?.lineOne ?? ''}
              onChange={(e) => updateAddress(index, 'lineOne', e.target.value)}
            />
            <Box display="flex" gap={1}>
              <TextField
                label="Town"
                fullWidth
                value={location.address?.town ?? ''}
                onChange={(e) => updateAddress(index, 'town', e.target.value)}
              />
              <TextField
                label="County"
                fullWidth
                value={location.address?.county ?? ''}
                onChange={(e) => updateAddress(index, 'county', e.target.value)}
              />
              <TextField
                label="Postcode"
                fullWidth
                value={location.address?.postCode ?? ''}
                onChange={(e) =>
                  updateAddress(index, 'postCode', e.target.value)
                }
              />
            </Box>
          </Stack>
        </Paper>
      ))}

      <Button
        startIcon={<AddRoundedIcon />}
        onClick={() => setLocations((prev) => [...prev, emptyBase()])}
        sx={{ alignSelf: 'flex-start' }}
      >
        Add Another Base
      </Button>
    </Stack>
  )
})

export default LocationStep
